import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { batchService } from '@/services/batchService'
import type { BatchFilters, CreateBatchRequest, UpdateBatchRequest } from '@/types/batch'

export function useAllBatches(filters: BatchFilters = {}) {
  return useQuery({
    queryKey: ['batches', 'all', filters],
    queryFn: () => batchService.getAllBatches(filters),
  })
}

export function useBatches(courseId: string, filters: BatchFilters = {}) {
  return useQuery({
    queryKey: ['batches', courseId, filters],
    queryFn: () => batchService.getBatches(courseId, filters),
    enabled: !!courseId,
  })
}

export function useBatch(id: string) {
  return useQuery({
    queryKey: ['batch', id],
    queryFn: () => batchService.getBatchById(id),
    enabled: !!id,
  })
}

export function useCreateBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateBatchRequest) => batchService.createBatch(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['batches', 'all'] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['batches', response.data.courseId] })
      }
    },
  })
}

export function useUpdateBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBatchRequest }) =>
      batchService.updateBatch(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['batches', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['batch', variables.id] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['batches', response.data.courseId] })
      }
    },
  })
}

export function useActivateBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => batchService.activateBatch(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['batches', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['batch', id] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['batches', response.data.courseId] })
      }
    },
  })
}

export function useCloseBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => batchService.closeBatch(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: ['batches', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['batch', id] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['batches', response.data.courseId] })
      }
    },
  })
}

export function useDeleteBatch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => batchService.deleteBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', 'all'] })
    },
  })
}
